import { CommonModule } from "@angular/common";
import { Component, DestroyRef, effect, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { Router, RouterOutlet } from "@angular/router";
import { AuthComponent } from "../../../auth/auth.component";
import { AuthContentDirective } from "../../../auth/directives/auth-content.directive";
import { AuthService } from "../../../auth/services/auth.service";
import { SidebarLayoutComponent } from "../sidebar-layout/sidebar-layout.component";
import { distinctUntilChanged, partition, shareReplay } from "rxjs";
import { LoggedUserService } from "../../services/logged-user.service";
import { AnalyticsService } from "../../services/analytics.service";
import { IntercomService } from "../../services/intercom.service";
import { AccountDeactivatedService } from "../../services/account-deactivated.service";
import { TutorialService } from "../../services/tutorial.service";
import { environment } from "../../../../environments/environment";

@Component({
  selector: "app-main-layout",
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    AuthComponent,
    AuthContentDirective,
    SidebarLayoutComponent,
  ],
  templateUrl: "./main-layout.component.html",
})
export class MainLayoutComponent implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);
  private readonly _loggedUserService = inject(LoggedUserService);
  private readonly _analyticsService = inject(AnalyticsService);
  private readonly _intercomService = inject(IntercomService);
  private readonly _accountDeactivatedService = inject(AccountDeactivatedService);
  private readonly _tutorialService = inject(TutorialService);

  /** Guard so the welcome dialog is requested at most once per session. */
  private _welcomeRequestedThisSession = false;

  protected readonly isAuthenticated$ = this._authService.isAuthenticated();

  /**
   * Current user signal - subscribing triggers lazy loading of user data
   * Only subscribed here (after authentication) to avoid loading before login
   */
  private readonly _currentUser = toSignal(this._loggedUserService.user$, {
    initialValue: null,
  });

  /**
   * Tenants signal - subscribing triggers lazy loading of tenants data
   */
  private readonly _tenants = toSignal(this._loggedUserService.tenants$, {
    initialValue: [],
  });

  /**
   * Selected tenant signal - used for Intercom initialization
   */
  private readonly _selectedTenant = toSignal(this._loggedUserService.selectedTenant$, {
    initialValue: null,
  });

  constructor() {
    // Watch for user loading and handle errors/analytics
    effect(() => {
      const user = this._currentUser();

      // If there's a load error (user authenticated but not in DB), navigate to error
      if (!user && this._loggedUserService.hasLoadError) {
        void this._router.navigate(['/error']);
        return;
      }

      // If the user is INACTIVE (deactivated server-side), route to the
      // dedicated page. Covers the case where deactivation happens while
      // the user is already in the app and a fresh user emission arrives.
      if (user?.status === 'INACTIVE') {
        this._accountDeactivatedService.signaled = true;
        void this._router.navigate(['/account-deactivated']);
        return;
      }

      // Identify user for analytics when loaded
      if (user) {
        this._analyticsService.identify(user);

        // Trigger the welcome dialog on first access. The flag is set
        // backend-side once the user dismisses the dialog (in any way).
        if (!user.hasAccessed && !this._welcomeRequestedThisSession) {
          this._welcomeRequestedThisSession = true;
          this._tutorialService.requestWelcome();
        }
      }
    });

    // Effect to watch user and tenant signals and manage Intercom lifecycle
    effect(() => {
      const user = this._currentUser();
      const tenant = this._selectedTenant();

      if (user && tenant) {
        // Both user and tenant available - initialize Intercom
        if (environment.intercomAppId) {
          void this._intercomService.initialize(user, tenant, environment.intercomAppId);
        }
      } else {
        // Either missing - shutdown Intercom
        this._intercomService.shutdown();
      }
    });
  }

  ngOnInit(): void {
    this._handleAuthNavigation();
  }

  private _handleAuthNavigation(): void {
    const [, logout$] = partition(
      this.isAuthenticated$.pipe(distinctUntilChanged(), shareReplay(1)),
      (value) => value,
    );

    // Login navigation is intentionally NOT handled here: the sign-in flow
    // navigates to "/" and the dynamic landing route decides the first available
    // module. A competing navigation here (e.g. to a non-existent "/home") would
    // race the landing redirect and leave a blank page.

    // When user logs out, clear location and navigate to root
    logout$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      // Force navigation to root to clear any protected routes from URL
      void this._router.navigate(["/"], { replaceUrl: true });
    });
  }
}
